import type { Component, JSX } from "solid-js";
import { createMemo, createEffect, createSignal, onCleanup, Show, splitProps } from "solid-js";
import type { IconName } from "../../icon/Icon";
import { Icon } from "../../icon/Icon";
import Handlebar from "../Handlebar";
import InlineEditor from "../../inlineEditor/InlineEditor";
import { useTranslation } from "../../../state/i18n";
import { isNil } from "lodash-es";
import { getIconCategoryColor } from "../../../utils/iconColorUtil";
import { Collapsible } from "@ark-ui/solid";
import type { ListItemMenuItems } from "../../listItemMenu/ListItemMenu";
import { IconButtonMenu } from "../../listItemMenu/ListItemMenu";
import { MAX_CHARACTERS_SM } from "../../../constants";
import { useDragDropContext } from "../../../utils/dragDropUtil";
import Text from "../../text/Text";
import { ErrorMessage } from "../../inputs/Input";
import { DynamicValueIconTile, IconTile } from "../../icon/IconTile";
import type { IconButtonProps } from "../../button/Button";
import { IconButton } from "../../button/Button";

type GenericPropertyContainerProps = {
  typeLabel: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  Content: Component;
  error: boolean;
  pillMenuComponent?: () => JSX.Element;
} & { draggable?: { id: string } };

type BasePropertyContainerProps = GenericPropertyContainerProps & {
  Title: {
    insideTrigger: Component;
    outsideTrigger?: Component;
  };
  Icon: Component;
  singleAction?: Pick<IconButtonProps, "aria-label" | "icon" | "onClick">;
  actions?: ListItemMenuItems<string>;
};
const TITLE_PADDING_RIGHT = 48;
const MENU_PILL_PADDING = 24;

const BasePropertyContainer: Component<BasePropertyContainerProps> = (props) => {
  const { t } = useTranslation();
  const [, { draggableActivators }] = useDragDropContext();
  let pillMenuRef: HTMLDivElement | undefined;
  const [dynamicPadding, setDynamicPadding] = createSignal(0);

  createEffect(() => {
    if (isNil(pillMenuRef)) {
      return;
    }
    const updatePadding = () => {
      const width = pillMenuRef?.offsetWidth ?? 0;
      setDynamicPadding(width + (width === 0 ? 0 : MENU_PILL_PADDING));
    };
    updatePadding();

    const observer = new MutationObserver(() => {
      updatePadding();
    });
    observer.observe(pillMenuRef, { attributes: true, childList: true, subtree: true });

    onCleanup(() => {
      observer.disconnect();
    });
  });

  return (
    <Collapsible.Root
      class="relative max-w-182 overflow-hidden rounded-sm border bg-surface-default has-[[data-container-trigger]:focus-visible]:outline-default"
      classList={{
        "border-subtle": !props.error,
        "border-error": props.error,
      }}
      open={props.open}
      onOpenChange={(state) => props.setOpen(state.open)}
      lazyMount
    >
      <Collapsible.Context>
        {(context) => (
          <>
            <Collapsible.Trigger
              data-container-trigger
              aria-label={
                context().open ? t(($) => $.COLLAPSE_CONFIGURATION_ARIA) : t(($) => $.EXPAND_CONFIGURATION_ARIA)
              }
              class="my-auto flex h-23 w-full items-center gap-2 p-6 text-left outline-none"
              classList={{ "hover:bg-surface-hover": !props.open }}
            >
              <props.Icon />

              <div
                class="flex grow flex-col overflow-hidden"
                style={{ "padding-right": `${dynamicPadding() + TITLE_PADDING_RIGHT}px` }}
              >
                <props.Title.insideTrigger />
              </div>
              <div
                class="flex size-5 shrink-0 rotate-0 items-center justify-center transition-transform duration-200 ease-linear"
                classList={{ "rotate-180": context().open }}
              >
                <Icon name="chevronDown" type="solid" size="lg" class="icon-primary" />
              </div>
            </Collapsible.Trigger>
            <Show when={props.draggable}>
              {(draggable) => (
                <div
                  class="absolute top-11.5 left-2 -translate-y-1/2 cursor-grab"
                  classList={{ hidden: context().open }}
                  {...draggableActivators(draggable().id, true)}
                >
                  <Handlebar expandDragArea />
                </div>
              )}
            </Show>
            <Show when={props.Title.outsideTrigger}>
              {(component) => <div class="absolute top-12 left-17.5 -translate-y-1/2">{component()({})}</div>}
            </Show>
            <div class="absolute top-11.5 right-14 flex -translate-y-1/2 gap-2">
              <div class="flex items-center" ref={(el) => (pillMenuRef = el)}>
                <Show when={props.pillMenuComponent}>{props.pillMenuComponent?.()}</Show>
              </div>
              <Show when={props.actions}>
                {(actions) => <IconButtonMenu aria-label={t(($) => $.ACTIONS_ARIA)} items={actions()} />}
              </Show>
              <Show when={props.singleAction}>
                {(action) => <IconButton {...action()} variant="tertiary" size="medium" />}
              </Show>
            </div>

            {/* oxlint-disable-next-line better-tailwindcss/no-restricted-classes */}
            <Collapsible.Content class="group grid grid-rows-[1fr] transition-all duration-250 ease-out closed:grid-rows-[0fr] closed:duration-200 closed:ease-in">
              <div class="flex flex-col gap-6 overflow-hidden px-6" inert={!context().open}>
                <props.Content />
                {/* This div is necessary in order to add padding at the bottom without breaking the animation */}
                <div />
              </div>
            </Collapsible.Content>
          </>
        )}
      </Collapsible.Context>
    </Collapsible.Root>
  );
};

type VariablePropertyContainerProps = GenericPropertyContainerProps & {
  label: string;
  icon: IconName;
  dataName: string;
  setDataName: (value: string) => void;
  dataNameError?: string;
  deleteItem: () => void;
  disabled?: boolean;
};

export const VariablePropertyContainer = (props: VariablePropertyContainerProps) => {
  const { t } = useTranslation();
  const [localProps, otherProps] = splitProps(props, [
    "label",
    "icon",
    "dataName",
    "setDataName",
    "dataNameError",
    "deleteItem",
    "Content",
    "disabled",
  ]);
  const [isEditing, setEditing] = createSignal<boolean>(false);
  const isDisabled = createMemo(() => localProps.disabled === true);

  return (
    <BasePropertyContainer
      singleAction={{
        "aria-label": t(($) => $.DELETE_VARIABLE_ACTION_ARIA),
        onClick: localProps.deleteItem,
        icon: "trash",
      }}
      Icon={() => (
        <DynamicValueIconTile
          icon={localProps.icon}
          iconClassList={{ [getIconCategoryColor("variable")]: true }}
          size="lg"
        />
      )}
      Title={{
        insideTrigger: () => (
          <>
            <Text class="text-base-medium text-primary" text={otherProps.typeLabel} truncate />
            <Text as="h3" class="text-medium-semibold text-primary" text={localProps.label} truncate />
          </>
        ),
      }}
      Content={() => (
        <>
          <div class="flex flex-col gap-2">
            <div class="flex h-7.5 items-center gap-1">
              <span class="shrink-0 text-base-medium text-secondary">{t(($) => $.DATA_NAME_LABEL)}</span>
              <div class="min-w-0 grow text-base-medium text-primary">
                <InlineEditor
                  value={localProps.dataName}
                  setValue={localProps.setDataName}
                  label={t(($) => $.DATA_NAME_LABEL)}
                  open={isEditing()}
                  setOpen={setEditing}
                  maxLength={MAX_CHARACTERS_SM}
                  displayView={() => (
                    <button
                      class="-ml-1 flex max-w-full items-center gap-1 rounded-sm bg-surface-subtle px-1 hover:bg-surface-subtle-hover focus-visible:outline-default disabled:pointer-events-none"
                      onClick={() => setEditing(true)}
                      title={localProps.dataName}
                      disabled={isDisabled()}
                    >
                      <Text text={localProps.dataName} truncate />
                      <Show when={!isDisabled()}>
                        <Icon name="pencil" size="sm" type="solid" class="shrink-0 icon-primary" />
                      </Show>
                    </button>
                  )}
                />
              </div>
            </div>
            <Show when={!isNil(localProps.dataNameError)}>
              <ErrorMessage errorMessage={localProps.dataNameError} />
            </Show>
          </div>
          <localProps.Content />
        </>
      )}
      {...otherProps}
    />
  );
};

type IntegrationPropertyContainerProps = GenericPropertyContainerProps & {
  label: string;
  icon: IconName;
  updateName: (value: string) => void;
  deleteItem: () => void;
};

export const IntegrationPropertyContainer = (props: IntegrationPropertyContainerProps) => {
  const { t } = useTranslation();
  const [localProps, otherProps] = splitProps(props, ["label", "icon", "deleteItem", "Content", "updateName"]);
  const [isEditingName, setEditingName] = createSignal<boolean>(false);

  return (
    <BasePropertyContainer
      actions={[
        [
          {
            value: "rename",
            type: "action",
            onClick: () => setEditingName(true),
            variant: "text",
            label: t(($) => $.RENAME_BUTTON),
          },
          {
            value: "delete",
            type: "action",
            onClick: localProps.deleteItem,
            variant: "text",
            label: t(($) => $.DELETE_BUTTON),
          },
        ],
      ]}
      Icon={() => <IconTile icon={localProps.icon} size="md" border />}
      Title={{
        insideTrigger: () => (
          <Show when={!isEditingName()}>
            <Text class="text-base-medium text-primary" text={otherProps.typeLabel} truncate />
            <Text as="h3" class="text-medium-semibold text-primary" text={localProps.label} truncate />
          </Show>
        ),
        outsideTrigger: () => (
          <Show when={isEditingName()}>
            <div class="w-123.5 text-medium-semibold text-primary">
              <InlineEditor
                value={localProps.label}
                setValue={localProps.updateName}
                label={t(($) => $.INTEGRATIONS_EDIT_NAME_LABEL)}
                open={isEditingName()}
                setOpen={setEditingName}
                maxLength={MAX_CHARACTERS_SM}
              />
            </div>
          </Show>
        ),
      }}
      Content={() => <localProps.Content />}
      {...otherProps}
    />
  );
};
